import { CubiqWorker, SKIP_PRIME } from "../../../worker/CubiqWorker";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BFXNotificationType } from "../../../model/core/notifications/BFXNotificationType";
import { BFXNotificationSubType } from "../../../model/core/notifications/BFXNotificationSubType";
import { BFXAppNotificationType } from "../../../model/core/notifications/BFXAppNotificationType";
import { BFXAppNotificationSubType } from "../../../model/core/notifications/BFXAppNotificationSubType";
import { BFXNotificationEventType } from "../../../model/core/notifications/BFXNotificationEventType";
import { BFXAppNotificationEventType } from "../../../model/core/notifications/BFXAppNotificationEventType";
import { TradeDealPrimaryLookupService } from "./TradeDealPrimaryLookupService";
import { Injectable } from "@angular/core";
import { ServiceMessageBrokerFactory } from "../../../../provider-webworker";
import { RawDataDealAdapter } from "../../../mediator/adapter/RawDataDealAdapter";
import { DealPrivatizationService } from "../../../model/privatization/DealPrivatizationService";
import { NotificationService } from "../../notification/impl/NotificationService";
import { NotificationTradeLookupService } from "./TradeDealLookupService";
import { TraderGroupPrimaryLookupService } from "./TraderGroupPrimaryLookupService";
import { OrderDataLookupService } from "./OrderDataLookupService";
import { BenchmarkDataLookupService } from "./BenchmarkDataLookupService";
import { HedgeOrderDataLookupService } from "./HedgeOrderDataLookupService";
import { BaseLookupService } from "../../impl/BaseLookupService";

@Injectable()
export class NotificationTradeDealPrimaryLookupService extends TradeDealPrimaryLookupService {
  protected blotterProfile: string[] = [];
  protected blotterType: string | symbol = SKIP_PRIME;
  protected allOrderLookupServices: BaseLookupService<string, CubiqTS.OrderDataImpl>[] = [];

  constructor(
    base: NotificationTradeLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataDealAdapter,
    notificationService: NotificationService,
    privatizationService: DealPrivatizationService,
    traderGroupLookupService: TraderGroupPrimaryLookupService,
    orderDataLookup: OrderDataLookupService,
    benchmarkDataLookup: BenchmarkDataLookupService,
    hedgeDataLookup: HedgeOrderDataLookupService,
  ) {
    super(
      base,
      factory,
      cubiqWorker,
      dataAdapter,
      notificationService,
      privatizationService,
      traderGroupLookupService,
    );

    this.allOrderLookupServices = [orderDataLookup, benchmarkDataLookup, hedgeDataLookup];
  }

  protected processDeal(tradeDeal: CubiqTS.DealImpl): void {
    if (
      tradeDeal.dealStateId !== undefined &&
      tradeDeal.dealStateId !== null &&
      tradeDeal.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
      tradeDeal.dealStateId !== CubiqTS.OrderDealState.FILL_CANCEL
    ) {
      let existingDeal = this.base.getTradeDeal(tradeDeal.id.toString());
      if (
        existingDeal &&
        existingDeal.dealStateId !== CubiqTS.OrderDealState.ACTIVE &&
        existingDeal.dealStateId !== CubiqTS.OrderDealState.FILL_PENDING &&
        tradeDeal.dealStateId === CubiqTS.OrderDealState.ACTIVE
      ) {
        // Deals can arrive out of sequence (https://www.bcdfx.com/ijira/browse/OMS-20851) so ignore any
        // ACTIVE deals when existing order is not ACTIVE or FILL_PENDING

        // the order of notification can sometimes come to us out of sync from server
        // we need to check if we are already in a complete state, if so we ignore the active notification
        // confirmed with KT - NB
        return;
      } else if (this.isPrimeEligible(tradeDeal)) {
        tradeDeal = this.privatizationService.checkPrivatization(tradeDeal);

        let notifType: BFXNotificationType = this.convertEventTypeToNotifType(
          tradeDeal.dealStateId,
        );

        let notifSubType: BFXNotificationSubType;
        let appNotifType: BFXAppNotificationType;
        let releaseFillAsOrder: boolean = false;

        if (!existingDeal && notifType == null) {
          //
          releaseFillAsOrder = CubiqTS.FillState.FILLED == tradeDeal.fillState;
          if (releaseFillAsOrder && !this.shouldNotifyAsPartFill(tradeDeal)) {
            appNotifType = BFXAppNotificationType.FILLED;
            notifType = BFXNotificationType.FILLED;
            notifSubType = BFXNotificationSubType.FILL_FULL;
          } else {
            appNotifType = BFXAppNotificationType.PART_FILLED;
            notifType = BFXNotificationType.PART_FILLED;
            notifSubType = BFXNotificationSubType.FILL_PART;
          }
        }

        this.fireModelUpdateEvent(tradeDeal);
        if (
          this.notifService.shouldNotify(notifType, tradeDeal.orderType) &&
          (notifType != BFXNotificationType.FILLED || this.shouldNotifyAsPartFill(tradeDeal))
        ) {
          if (notifType != null) {
            if (!existingDeal)
              this.notifService.fireNotificationEvent(
                this.notifService.tradeDataToNotification(tradeDeal, notifType, notifSubType),
                BFXNotificationEventType.TRADE_UPDATE_EVENT,
              );
            else if (
              existingDeal &&
              existingDeal.dealStateId === CubiqTS.OrderDealState.ACK_TIME_OUT
            )
              this.notifService.fireNotificationEvent(
                this.notifService.tradeDataToNotification(
                  existingDeal,
                  BFXNotificationType.DEAL_FAILURE,
                  null,
                ),
                BFXNotificationEventType.TRADE_UPDATE_EVENT,
              );
          } else {
            this.fireModelNotificationClearEvent([tradeDeal.id.toString()]);
          }
        } else if (
          tradeDeal.dealStateId === CubiqTS.OrderDealState.COMPLETED &&
          existingDeal != null &&
          (existingDeal.dealStateId === CubiqTS.OrderDealState.NAK ||
            existingDeal.dealStateId === CubiqTS.OrderDealState.ACK_TIME_OUT)
        ) {
          //USED FOR OPS INTERVENTION MANUAL CLEAR FLOW
          this.fireModelNotificationClearEvent([tradeDeal.id.toString()]);
        }

        if (
          appNotifType === BFXAppNotificationType.PART_FILLED &&
          this.notifService.shouldAppNotify(appNotifType, tradeDeal.orderType)
        ) {
          this.notifService.fireAppNotificationEvent(
            this.notifService.tradeDataToNotification(tradeDeal, notifType, notifSubType),
            BFXAppNotificationEventType.DEAL_UPDATE_EVENT,
          );
        }

        if (releaseFillAsOrder) {
          this.notifService.fireQueuedNotificationEvent(tradeDeal.orderId.toString());
          this.notifService.fireQueuedAppNotificationEvent(tradeDeal.orderId.toString());
        }
      }
    }
  }

  filterEvent(_: CubiqTS.ConcreteEncodable): boolean {
    return true;
  }

  isPrimeEligible(_: CubiqTS.DealImpl): boolean {
    return true;
  }

  public primePostProcess(): void {
    return;
  }

  convertEventTypeToNotifType(eventType: CubiqTS.OrderDealState): BFXNotificationType {
    switch (eventType) {
      case CubiqTS.OrderDealState.ACK_TIME_OUT:
        return BFXNotificationType.DEAL_FAILURE;
      case CubiqTS.OrderDealState.NAK:
        return BFXNotificationType.DEAL_FAILURE;
      default:
        return;
    }
  }

  public shouldNotifyAsPartFill(deal: CubiqTS.DealImpl): boolean {
    let partFill: boolean = deal.isAggregateDealFg != true;

    if (partFill) {
      let orderData: CubiqTS.OrderDataImpl = null;

      let orderId: string = deal.orderId.toString();
      this.allOrderLookupServices.find((a) => (orderData = a.getItem(orderId)) != null);

      if (orderData != null) {
        partFill = !orderData.orderQty.isEqualTo(deal.amt1);
      }
    }

    return partFill;
  }
}
